import { COMPANY_MESSAGES, QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import {
  AccountingPeriod,
  AccountingPeriodInput,
  BankInfo,
  BankInfoInput,
  Company,
  CompanyFinancialStatement,
  CompanyFinancialStatementCreateInput,
  CompanyFinancialStatementUpdateInput,
  CompanyInvoice,
  CompanyInvoiceInput,
  CompanyPhoneNumber,
  CompanyPhoneNumberInput,
  CompanyRegistrationInfo,
  CompanyRegistrationInfoInput,
  CompanyRegulartion,
  CompanyRegulartionCreateInput,
  CompanyRegulartionUpdateInput,
  CompanyShareRecord,
  IndustryCategories,
  MultipleCompanyDataInput,
  TaxInfo,
  TaxInfoInput,
} from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notification } from "antd";
import { isEmpty } from "lodash";
import { useReAuthenticationOneTime } from "./auth";
import axiosInstanceV2 from "@lhd/libs/axiosV2";

export const useFetchCompanies = (
  page: any,
  pageSize: any,
  filterParams: any,
) => {
  return useQuery(
    [QUERY_NAME.COMPANIES, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.COMPANIES,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          scope: "list_all",
        },
      }).then((res) => {
        return {
          data: res.data as Company[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      cacheTime: 0,
      staleTime: Infinity,
    },
  );
};

export const useFetchV2Companies = (
  page: any,
  pageSize: any,
  filterParams: any,
  selectedTabs?: any,
) => {
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};
  return useQuery(
    [QUERY_NAME.COMPANIES, page, pageSize, restFilterParams, selectedTabs],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANIES}/${selectedTabs}`,
        params: {
          page,
          per_page: pageSize,
          ...restFilterParams,
          // scope: "list_all",
        },
      }).then((res) => {
        return {
          data: res.data as Company[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      cacheTime: 0,
      staleTime: Infinity,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useSearchCompanies = (searchKeyword: string) => {
  return useQuery(
    [QUERY_NAME.COMPANIES, "search", searchKeyword],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANIES}/search`,
        params: {
          page: 1,
          per_page: 100,
          name: searchKeyword,
        },
      }).then((res) => {
        return {
          data: res.data as Company[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!searchKeyword && searchKeyword.trim().length > 0,
      cacheTime: 0,
      staleTime: 0,
    },
  );
};

export const useFetchIndustryCategories = (page: any, pageSize: any) => {
  return useQuery(
    [QUERY_NAME.COMPANIES + "industry_categories", page, pageSize],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.COMPANIES}/industry_categories`,
        params: {
          page,
          per_page: pageSize,
        },
      }).then((res) => {
        return {
          data: res.data as IndustryCategories[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
  );
};

export const useFetchOneCompany = (companyId: any) => {
  return useQuery(
    [QUERY_NAME.COMPANY, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.COMPANIES}/${companyId}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!companyId,
    },
  );
};

export const useSubmitCompany = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, companyId, method, headers }: any) =>
      axios({
        method: method ? method : "put",
        url: `${SERVICES.COMPANIES}/${companyId}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ companyId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.COMPANIES}/${companyId}`,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANIES]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleCompany = () => {
  const queryClient = useQueryClient();
  const { handleAuthen } = useReAuthenticationOneTime(false);
  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.COMPANIES}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANIES]);
        const companyIdLogined = getCompanyId();
        handleAuthen(companyIdLogined);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useFetchCompanyPositions = () => {
  return useQuery(
    [QUERY_NAME.COMPANY],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.COMPANY_MENU_SETTINGS}${SERVICES.COMPANIES}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useUpdatePositionCompanies = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (companyIds: number[]) =>
      axios({
        method: "post",
        url: `${SERVICES.COMPANY_MENU_SETTINGS}/company_positions`,
        data: { company_ids: companyIds },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY]);
      },
    },
  );
};

// tab2
export const useAddNewCompanyInvoice = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: CompanyInvoiceInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_INOICES}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useUpdateCompanyInvoice = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyInvoiceInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_INOICES}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.INVOICE_LIST]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useDeleteCompanyInvoice = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_INOICES}/${id}`,
        params: { company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_INVOICE_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.INVOICE_LIST]);
        onError && onError(error);
      },
    },
  );
};
//bank info
export const useAddNewCompanyBankInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: BankInfoInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_BANK_INFOS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_BANK_INFO_SUCCESS,
        });
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};
export const useUpdateCompanyBankInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<BankInfoInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_BANK_INFOS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_BANK_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useDeleteCompanyBankInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_BANK_INFOS}/${id}`,
        params: { company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_BANK_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.BANK_INFOS]);
        onError && onError(error);
      },
    },
  );
};
//section 4 tab 2
export const useAddNewCompanyRegistrationInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: CompanyRegistrationInfoInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGISTRATION_INFOS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useUpdateCompanyRegistrationInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyRegistrationInfoInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGISTRATION_INFOS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGISTRATION_INFOS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useDeleteCompanyRegistrationInfo = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGISTRATION_INFOS}/${id}`,
        params: { company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_REGISTRATION_INFO_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGISTRATION_INFOS]);
        onError && onError(error);
      },
    },
  );
};
//section 5 tab 2
export const useAddNewCompanyAccountingPeriod = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: AccountingPeriodInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_ACCOUNTING_PERIODS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useUpdateCompanyAccountingPeriod = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<AccountingPeriodInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_ACCOUNTING_PERIODS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_ACCOUNTING_PERIODS]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useDeleteCompanyAccountingPeriod = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_ACCOUNTING_PERIODS}/${id}`,
        params: { company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_ACCOUNTING_PERIOD_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_ACCOUNTING_PERIODS]);
        onError && onError(error);
      },
    },
  );
};
//section 6 tab 2
export const useAddNewCompanyTaxHistories = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: TaxInfoInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V1,
        url: `${SERVICES.COMPANY_TAX_HISTORIES}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.ADD_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useUpdateCompanyTaxHistories = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<TaxInfoInput>;
    }) =>
      axios({
        method: "put",
        baseURL: process.env.REACT_APP_API_URL_V1,
        url: `${SERVICES.COMPANY_TAX_HISTORIES}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_TAX_HISTORIES]);
        notification["success"]({
          message: COMPANY_MESSAGES.UPDATE_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};
export const useDeleteCompanyTaxHistories = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V1,
        url: `${SERVICES.COMPANY_TAX_HISTORIES}/${id}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess: () => {
        notification["success"]({
          message: COMPANY_MESSAGES.DELETE_TAX_HISTORIES_SUCCESS,
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_TAX_HISTORIES]);
        onError && onError(error);
      },
    },
  );
};

export const useMultipleCompanyData = ({
  paginations,
  companyId,
}: MultipleCompanyDataInput) => {
  const results = useQueries({
    queries: [
      // Query 1: Invoice List
      {
        queryKey: [
          QUERY_NAME.INVOICE_LIST,
          companyId,
          paginations.companyInvoicesPagination.page,
          paginations.companyInvoicesPagination.pageSize,
        ],
        queryFn: () =>
          axios({
            method: "get",
            url: `${SERVICES.COMPANY_INOICES}`,
            baseURL: process.env.REACT_APP_API_URL_V2,
            params: {
              company_id: companyId,
              page: paginations.companyInvoicesPagination.page,
              per_page: paginations.companyInvoicesPagination.pageSize,
            },
          }).then((res) => ({
            data: res.data as CompanyInvoice[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 2: Company Invoices
      {
        queryKey: [
          QUERY_NAME.BANK_INFOS,
          companyId,
          paginations.bankInfosPagination.page,
          paginations.bankInfosPagination.pageSize,
        ],
        queryFn: () =>
          axios({
            method: "get",
            baseURL: process.env.REACT_APP_API_URL_V2,
            url: `${SERVICES.COMPANY_BANK_INFOS}`,
            params: {
              company_id: companyId,
              page: paginations.bankInfosPagination.page,
              per_page: paginations.bankInfosPagination.pageSize,
            },
          }).then((res) => ({
            data: res.data as BankInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 3: registrationInfo
      {
        queryKey: [
          QUERY_NAME.COMPANY_REGISTRATION_INFOS,
          companyId,
          paginations.registrationInfosPagination.page,
          paginations.registrationInfosPagination.pageSize,
        ],
        queryFn: () =>
          axios({
            method: "get",
            url: `${SERVICES.COMPANY_REGISTRATION_INFOS}`,
            baseURL: process.env.REACT_APP_API_URL_V2,
            params: {
              company_id: companyId,
              page: paginations.registrationInfosPagination.page,
              per_page: paginations.registrationInfosPagination.pageSize,
            },
          }).then((res) => ({
            data: res.data as CompanyRegistrationInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 4: accountingPeriods
      {
        queryKey: [
          QUERY_NAME.COMPANY_ACCOUNTING_PERIODS,
          companyId,
          paginations.accountingPeriodsPagination.page,
          paginations.accountingPeriodsPagination.pageSize,
        ],
        queryFn: () =>
          axios({
            method: "get",
            baseURL: process.env.REACT_APP_API_URL_V2,
            url: `${SERVICES.COMPANY_ACCOUNTING_PERIODS}`,
            params: {
              company_id: companyId,
              page: paginations.accountingPeriodsPagination.page,
              per_page: paginations.accountingPeriodsPagination.pageSize,
            },
          }).then((res) => ({
            data: res.data as AccountingPeriod[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 5: tax
      {
        queryKey: [
          QUERY_NAME.COMPANY_TAX_HISTORIES,
          companyId,
          paginations.taxsPagination.page,
          paginations.taxsPagination.pageSize,
        ],
        queryFn: () =>
          axios({
            method: "get",
            url: `${SERVICES.COMPANY_TAX_HISTORIES}`,
            params: {
              company_id: companyId,
              page: paginations.taxsPagination.page,
              per_page: paginations.taxsPagination.pageSize,
            },
          }).then((res) => ({
            data: res.data as TaxInfo[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 6: company phone numbers
      {
        queryKey: [
          QUERY_NAME.COMPANY_PHONE_NUMBERS,
          companyId,
          paginations.companyPhoneNumbersPagination?.page || 1,
          paginations.companyPhoneNumbersPagination?.pageSize || 5,
        ],
        queryFn: () =>
          axios({
            method: "get",
            baseURL: process.env.REACT_APP_API_URL_V2,
            url: `${SERVICES.COMPANY_PHONE_NUMBERS}`,
            params: {
              company_id: companyId,
              page: paginations.companyPhoneNumbersPagination?.page || 1,
              per_page:
                paginations.companyPhoneNumbersPagination?.pageSize || 5,
            },
          }).then((res) => ({
            data: res.data as CompanyPhoneNumber[],
            meta: {
              total: res.headers["x-total"] || 0,
              total_page: res.headers["x-total-pages"] || 0,
            },
          })),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
      // Query 7: tax config
      {
        queryKey: [QUERY_NAME.COMPANIES + "industry_categories", 1, 1000000],
        queryFn: () =>
          axios({
            method: "get",
            url: `${SERVICES.COMPANIES}/industry_categories`,
            params: {
              page: 1,
              per_page: 1000000,
            },
          }).then((res) => {
            return {
              data: res.data as IndustryCategories[],
              meta: {
                total: res.headers["x-total"] || 0,
                total_page: res.headers["x-total-pages"] || 0,
              },
            };
          }),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    ],
  });

  return {
    companyInvoices: {
      data: results[0]?.data?.data || [],
      meta: results[0]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[0]?.isLoading || false : false,
      isError: results[0]?.isError || false,
      error: results[0]?.error,
      dataUpdatedAt: results[0]?.dataUpdatedAt,
    },
    bankInfos: {
      data: results[1]?.data?.data || [],
      meta: results[1]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[1]?.isLoading || false : false,
      isError: results[1]?.isError || false,
      error: results[1]?.error,
      dataUpdatedAt: results[1]?.dataUpdatedAt,
    },
    registrationInfos: {
      data: results[2]?.data?.data || [],
      meta: results[2]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[2]?.isLoading || false : false,
      isError: results[2]?.isError || false,
      error: results[2]?.error,
      dataUpdatedAt: results[2]?.dataUpdatedAt,
    },
    accountingPeriods: {
      data: results[3]?.data?.data || [],
      meta: results[3]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[3]?.isLoading || false : false,
      isError: results[3]?.isError || false,
      error: results[3]?.error,
      dataUpdatedAt: results[3]?.dataUpdatedAt,
    },
    taxInfos: {
      data: results[4]?.data?.data || [],
      meta: results[4]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[4]?.isLoading || false : false,
      isError: results[4]?.isError || false,
      error: results[4]?.error,
      dataUpdatedAt: results[4]?.dataUpdatedAt,
    },
    companyPhoneNumbers: {
      data: results[5]?.data?.data || [],
      meta: results[5]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[5]?.isLoading || false : false,
      isError: results[5]?.isError || false,
      error: results[5]?.error,
      dataUpdatedAt: results[5]?.dataUpdatedAt,
    },
    industryCategories: {
      data: results[6]?.data?.data || [],
      meta: results[6]?.data?.meta || { total: 0, total_page: 0 },
      isLoading: companyId ? results[6]?.isLoading || false : false,
      isError: results[6]?.isError || false,
      error: results[6]?.error,
      dataUpdatedAt: results[6]?.dataUpdatedAt,
      refetch: results[6]?.refetch,
    },
    isAnyLoading: results.some((result) => result.isLoading),
    isAnyError: results.some((result) => result.isError),
    allErrors: results.map((result) => result.error).filter(Boolean),
  };
};
export const useFetchBankInfos = (
  page: number,
  pageSize: number,
  companyId: string,
) => {
  return useQuery(
    [QUERY_NAME.BANK_INFOS, companyId, page, pageSize],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_BANK_INFOS}`,
        params: {
          company_id: companyId,
          page: page,
          per_page: pageSize,
        },
      }).then((res) => ({
        data: res.data as BankInfo[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      enabled: !!companyId,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
};

// for tab 3
export const useFetchCompanyRegulations = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
) => {
  return useQuery(
    [QUERY_NAME.COMPANY_REGULATIONS, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}`,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyRegulartion[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
  );
};

interface RecordType {
  [key: string]: any;
}
export const useSubmitSaveCompany = (id: string) => {
  const queryClient = useQueryClient();
  const companyId = id || getCompanyId();

  return useMutation(
    ({ payload }: { payload: { [key: string]: any } }) => {
      const {
        create_records = [],
        update_records = [],
        deleted_ids = [],
      } = payload;
      const formData = new FormData();
      if (!isEmpty(create_records)) {
        create_records.forEach((record: RecordType) => {
          Object.entries(record).forEach(([key, value]) => {
            formData.append(`create_records[][${key}]`, String(value));
          });
        });
      }

      if (!isEmpty(update_records)) {
        update_records.forEach((record: RecordType) => {
          Object.entries(record).forEach(([key, value]) => {
            formData.append(`update_records[][${key}]`, String(value));
          });
        });
      }
      if (!isEmpty(deleted_ids)) {
        deleted_ids.forEach((id: any) => {
          formData.append("deleted_ids[]", String(id));
        });
      }

      formData.append("company_id", String(companyId));

      return axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: SERVICES.COMPANY_REGULATIONS_SAVE,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess(e) {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);
        if (e?.data?.errors?.length > 0) {
          notification["error"]({
            message: e?.data?.errors?.[0],
          });
        } else {
          // notification['success']({ message: '保存しました' });
        }
      },
    },
  );
};

export const useSubmitSaveFinancialCompany = (id: string) => {
  const queryClient = useQueryClient();
  const companyId = id || getCompanyId();

  return useMutation(
    ({ payload }: { payload: { [key: string]: any } }) => {
      const {
        create_records = [],
        update_records = [],
        deleted_ids = [],
      } = payload;
      const formData = new FormData();
      if (!isEmpty(create_records)) {
        create_records.forEach((record: RecordType) => {
          Object.entries(record).forEach(([key, value]) => {
            formData.append(`create_records[][${key}]`, String(value));
          });
        });
      }

      if (!isEmpty(update_records)) {
        update_records.forEach((record: RecordType) => {
          Object.entries(record).forEach(([key, value]) => {
            formData.append(`update_records[][${key}]`, String(value));
          });
        });
      }
      if (!isEmpty(deleted_ids)) {
        deleted_ids.forEach((id: any) => {
          formData.append("deleted_ids[]", String(id));
        });
      }

      formData.append("company_id", String(companyId));

      return axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: SERVICES.COMPANY_FINANCIAL_SAVE,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess(e) {
        queryClient.invalidateQueries([
          QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
        ]);
        if (e?.data?.errors?.length > 0) {
          notification["error"]({
            message: e?.data?.errors?.[0],
          });
        } else {
          // notification['success']({ message: '保存しました' });
        }
      },
    },
  );
};

export const useFetchCompanyRegulationDetail = (id: number) => {
  return useQuery(
    [QUERY_NAME.COMPANY_REGULATIONS, id],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}/${id}`,
      }).then((res) => ({
        data: res.data as CompanyRegulartion,
      })),
    {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};
export const useCreateCompanyRegulation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload }: { payload: CompanyRegulartionCreateInput }) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        data: payload,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};
export const useUpdateCompanyRegulation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: CompanyRegulartionUpdateInput;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}/${id}`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        data: payload,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};
export const useDeleteCompanyRegulation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}/${id}`,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};

export const useDeleteMultipleCompanyRegulation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ listCompanyRegulationIds, companyId }: any) => {
      const formData = new FormData();
      listCompanyRegulationIds.forEach((id: any) => {
        formData.append(`deleted_ids[]`, id);
      });
      formData.append("company_id", companyId);
      return axios({
        method: "post",
        data: formData,
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_REGULATIONS}/save`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.invalidateQueries([QUERY_NAME.COMPANY_REGULATIONS]);
          notification["success"]({
            message: "保存しました",
          });
        }
      },
    },
  );
};

//tab4
export const useFetchCompanyFinancialStatement = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
) => {
  return useQuery(
    [QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}`,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
        },
      }).then((res) => {
        return {
          data: res.data as CompanyFinancialStatement[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
  );
};
export const useFetchCompanyFinancialStatementDetail = (id: number) => {
  return useQuery(
    [QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS, id],
    () =>
      axios({
        method: "get",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}/${id}`,
      }).then((res) => ({
        data: res.data as CompanyFinancialStatement,
      })),
    {
      enabled: !!id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};
export const useCreateCompanyFinancialStatement = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload }: { payload: CompanyFinancialStatementCreateInput }) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}`,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: payload,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};
export const useUpdateCompanyFinancialStatement = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyFinancialStatementUpdateInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}/${id}`,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: payload,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};
export const useDeleteCompanyFinancialStatement = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}/${id}`,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
        ]);
        notification["success"]({
          message: "財務諸表の削除に成功しました",
        });
      },
      onError: (error: any) => {
        notification["error"]({
          message: error?.response?.data?.error,
        });
      },
    },
  );
};

export const useDeleteMultipleCompanyFinancialStatement = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listCompanyFinancialStatementIds, companyId }: any) => {
      const formData = new FormData();
      listCompanyFinancialStatementIds.forEach((id: any) => {
        formData.append(`deleted_ids[]`, id);
      });
      formData.append(`company_id`, companyId);
      return axios({
        method: "post",
        data: formData,
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_FINANCIAL_STATEMENTS}/save`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess(e) {
        if (e?.data?.errors?.length) {
          const msgError = e?.data?.errors;
          queryClient.invalidateQueries([
            QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
          ]);

          notification["error"]({
            message: msgError?.[0]?.msg || "保存に失敗しました。",
          });
        } else {
          queryClient.invalidateQueries([
            QUERY_NAME.COMPANY_FINANCIAL_STATEMENTS,
          ]);
          notification["success"]({
            message: "保存しました",
          });
        }
      },
    },
  );
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, companyId }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.COMPANIES}/${companyId}/update_station_id`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANIES + "detail"]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        notification["error"]({
          message: error?.response?.data?.msg,
        });
      },
    },
  );
};
export const useGetCompanyDetail = ({ companyId }: { companyId?: string }) => {
  return useQuery(
    [QUERY_NAME.COMPANIES + "detail", companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.COMPANIES}/${companyId}`,
      }).then((res) => ({
        data: res.data as Company,
      })),
    {
      enabled: !!companyId,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};
export const useFetchListCompany = (
  app_id: string,
  app_record_ids: string[],
) => {
  return useQuery(
    [QUERY_NAME.COMPANIES, "list_company", app_id, app_record_ids],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPS}/${app_id}/${SERVICES.LIST_COMPANIES}`,
        params: {
          app_record_ids: app_record_ids,
        },
      }).then((res) => res.data as CompanyShareRecord),
    {
      enabled: !!app_record_ids.length || !!app_id,
      cacheTime: 0,
      staleTime: 0,
    },
  );
};

// Company Phone Number hooks
export const useAddNewCompanyPhoneNumber = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  return useMutation(
    (payload: CompanyPhoneNumberInput) =>
      axios({
        method: "post",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_PHONE_NUMBERS}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "電話番号を追加しました",
        });
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

export const useUpdateCompanyPhoneNumber = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CompanyPhoneNumberInput>;
    }) =>
      axios({
        method: "patch",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_PHONE_NUMBERS}/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_PHONE_NUMBERS]);
        notification["success"]({
          message: "電話番号を更新しました",
        });
      },
      onError: (error: any) => {
        onError && onError(error);
      },
    },
  );
};

export const useDeleteCompanyPhoneNumber = ({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number | string) =>
      axios({
        method: "delete",
        baseURL: process.env.REACT_APP_API_URL_V2,
        url: `${SERVICES.COMPANY_PHONE_NUMBERS}/${id}`,
        params: { company_id: companyId },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_PHONE_NUMBERS]);
        notification["success"]({
          message: "電話番号を削除しました",
        });
      },
      onError: (error: any) => {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_PHONE_NUMBERS]);
        onError && onError(error);
      },
    },
  );
};

export const useFetchCompaniesV2 = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.COMPANIES, companyId],
    () =>
      axios({
        method: "get",
        url: SERVICES.COMPANY_V2,
        params: {
          login_company_id: companyId,
        },
      }).then((res) => {
        return {
          data: res.data as Company[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      cacheTime: 0,
      staleTime: Infinity,
    },
  );
};
